import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Chip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';

const AppointmentConfirmationPage = () => {
  const { appointmentId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState(null);
  const [appointmentDetails, setAppointmentDetails] = useState(null);
  const [alreadyConfirmed, setAlreadyConfirmed] = useState(false);

  useEffect(() => {
    const confirmAppointment = async () => {
      if (!appointmentId || !token) {
        setError('Invalid confirmation link. Please check your email and try again.');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'https://api.inkedupmanager.com'}/appointments/${appointmentId}/confirm/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        });

        const data = await response.json();

        if (data.success) {
          setConfirmed(true);
          setAppointmentDetails(data.appointment_details);
          
          // Check if it was already confirmed
          if (data.message && data.message.includes('already confirmed')) {
            setAlreadyConfirmed(true);
          }
        } else {
          setError(data.error || 'Failed to confirm appointment. Please try again.');
        }
      } catch (err) {
        console.error('Error confirming appointment:', err);
        setError('Network error. Please check your connection and try again.');
      } finally {
        setLoading(false);
      }
    };

    confirmAppointment();
  }, [appointmentId, token]);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6">Confirming your appointment...</Typography>
        </Paper>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <ErrorIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom color="error">
            Confirmation Failed
          </Typography>
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        </Paper>
      </Container>
    );
  }

  if (confirmed && appointmentDetails) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          {/* Success Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom color="success.main">
              {alreadyConfirmed ? 'Already Confirmed!' : 'Appointment Confirmed!'}
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {alreadyConfirmed 
                ? 'Your appointment was previously confirmed.'
                : 'Your appointment has been successfully confirmed.'
              }
            </Typography>
          </Box>

          {/* Appointment Details Card */}
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CalendarTodayIcon sx={{ mr: 1 }} />
                Appointment Details
              </Typography>
              
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'grid', gap: 2 }}>
                {/* Date and Time */}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CalendarTodayIcon sx={{ mr: 2, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      Date: {appointmentDetails.date}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AccessTimeIcon sx={{ mr: 2, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      Time: {appointmentDetails.time}
                    </Typography>
                  </Box>
                </Box>

                {/* Studio Information */}
                {appointmentDetails.studio_name && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <BusinessIcon sx={{ mr: 2, color: 'primary.main' }} />
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        Studio: {appointmentDetails.studio_name}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Artist Information */}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PersonIcon sx={{ mr: 2, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      Artist: {appointmentDetails.artist_name}
                    </Typography>
                  </Box>
                </Box>

                {/* Duration */}
                {appointmentDetails.duration && (
                  <Box sx={{ pl: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Duration: {appointmentDetails.duration} minutes
                    </Typography>
                  </Box>
                )}

                {/* Notes */}
                {appointmentDetails.notes && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Notes:</strong> {appointmentDetails.notes}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Status Chip */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Chip 
              label="CONFIRMED" 
              color="success" 
              variant="filled" 
              sx={{ fontSize: '1rem', px: 2, py: 1 }}
            />
          </Box>

          {/* Additional Information */}
          <Alert severity="success" sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>What's next?</strong> Your appointment is confirmed! Please arrive on time and bring any 
              necessary identification or materials. If you need to make changes or have questions, 
              please contact {appointmentDetails.studio_name || 'the studio'} directly.
            </Typography>
          </Alert>

          {/* Contact Information Note */}
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              <strong>Need to make changes?</strong> Please contact{' '}
              {appointmentDetails.studio_name || 'your artist'} directly to reschedule or modify your appointment.
            </Typography>
          </Box>
        </Paper>
      </Container>
    );
  }

  return null;
};

export default AppointmentConfirmationPage;